/**
 * Wrist menu: where you are and who is with you, teleport shortcuts, your voice and hand,
 * comfort settings, the room's collaboration tools, reactions and leaving the room.
 *
 * One glass canvas panel in the design language (one draw call; the three-mesh-ui version
 * cost a draw call per button, about thirty). Sections with small caps headings, tiles with
 * drawn line icons that glow cyan while a mode is on, a segmented control for snap turn,
 * round reaction chips and a leave bar. The room-only section is left out in the lobby and
 * the panel shortens to fit.
 *
 * In VR it sits above the left wrist and toggles with the left X button (or a double pinch
 * with hand tracking); on desktop Tab opens it in front of the camera.
 *
 * Without a controller there is no X button (Apple Vision Pro's gaze and pinch, or hand
 * tracking on the Quest, 2026-10-07): a small Menu orb floats low on the left of the view and
 * a pinch on it opens the menu, which then stands still in front of the user, since there is
 * no wrist to follow.
 */
import * as THREE from 'three';
import type { Reaction } from '@clinspace/shared';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import { fillRoundRect, font, glassPanel, text, PALETTE, inkFor } from '../dashboards/charts.ts';
import type { Interactive, InteractiveSystem } from './Interactive.ts';
import { GLASS } from './theme.ts';

export interface TeleportAnchor {
  label: string;
  position: THREE.Vector3;
}

export interface WristMenuActions {
  teleport(position: THREE.Vector3): void;
  toggleMute(): boolean;
  recalibrate(): void;
  setSnapAngle(deg: number): void;
  toggleSmoothLocomotion(): boolean;
  toggleDebugHud(): boolean;
  leaveRoom(): void;
  isMuted(): boolean;
  snapAngle(): number;
  smoothLocomotion(): boolean;
  debugHud(): boolean;
  /* Spatial collaboration (Phase 3.5); no-ops outside a study room. */
  toggleAnnotate(): boolean;
  togglePoint(): boolean;
  cycleFollow(): string | undefined;
  tidyRoom(): void;
  annotating(): boolean;
  pointing(): boolean;
  following(): string | undefined;
  /* Meeting tools (Phase 6). */
  togglePresent(): void;
  toggleShare(): void;
  pullScreen(): void;
  toggleHand(): boolean;
  presenting(): boolean;
  sharing(): boolean;
  handRaised(): boolean;
  canShare(): boolean;
  react(reaction: Reaction): void;
}

/** Panel size: metres and logical pixels (the backing canvas is 1.25x for the headset). */
const W_M = 0.44;
const PX_W = 880;
const PX_H = 1300;
const H_M = (W_M * PX_H) / PX_W;
const PAD = 36;

type Icon =
  | 'go'
  | 'mic'
  | 'micOff'
  | 'hand'
  | 'height'
  | 'turn'
  | 'walk'
  | 'gauge'
  | 'pen'
  | 'pointer'
  | 'follow'
  | 'tidy'
  | 'present'
  | 'share'
  | 'pull'
  | 'exit';

interface Tile {
  id: string;
  label: string;
  icon: Icon;
  on?: boolean;
  enabled?: boolean;
  action: () => void;
  hint?: string;
}

const REACTIONS: { label: string; reaction: Reaction; glyph: string }[] = [
  { label: '+1', reaction: 'thumbsUp', glyph: '+1' },
  { label: 'Clap', reaction: 'clap', glyph: 'Clap' },
  { label: 'Wave', reaction: 'wave', glyph: 'Wave' },
  { label: '?', reaction: 'question', glyph: '?' },
];

/** The camera's facing on the floor plane (where it looks, without the pitch). */
function levelForward(camera: THREE.Camera): THREE.Vector3 {
  const q = camera.getWorldQuaternion(new THREE.Quaternion());
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
  forward.y = 0;
  if (forward.lengthSq() < 1e-6) forward.set(0, 0, -1);
  return forward.normalize();
}

/** A round glass button with the three-line menu mark, drawn once on a small canvas. */
function makeOrb(): THREE.Mesh {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const c = size / 2;
    ctx.beginPath();
    ctx.arc(c, c, c - 6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(11,15,21,0.82)';
    ctx.fill();
    ctx.lineWidth = 6;
    ctx.strokeStyle = '#3fd6ff';
    ctx.stroke();
    ctx.lineCap = 'round';
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#f2f6fb';
    for (const dy of [-18, 0, 18]) {
      ctx.beginPath();
      ctx.moveTo(c - 24, c + dy);
      ctx.lineTo(c + 24, c + dy);
      ctx.stroke();
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  const orb = new THREE.Mesh(
    new THREE.CircleGeometry(0.032, 40),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      toneMapped: false,
    }),
  );
  orb.name = 'menu-orb';
  orb.renderOrder = 901;
  orb.visible = false;
  return orb;
}

export class WristMenu {
  readonly group = new THREE.Group();
  readonly panel: CanvasPanel;
  private anchors: TeleportAnchor[] = [];
  private canLeave = false;
  /** Where you are, for the header (a study room gives its room code). */
  private where: string | undefined;
  private status = '';
  /** Every action by its current label, as last drawn (dev hooks press by label). */
  private actionsByLabel = new Map<string, () => void>();
  private readonly regionCentres = new Map<string, { x: number; y: number }>();
  open = false;
  /** The Menu orb for headsets without a menu button; add to the scene beside `group`. */
  readonly orb: THREE.Mesh;
  private readonly orbItem: Interactive;
  private orbPlaced = false;
  /** Opened without a wrist to follow: placed once in front of the user, then left. */
  private freePlaced = false;

  constructor(
    private readonly system: InteractiveSystem,
    private readonly actions: WristMenuActions,
  ) {
    this.group.name = 'wrist-menu';
    this.panel = new CanvasPanel({
      width: W_M,
      height: H_M,
      pixelWidth: PX_W,
      pixelHeight: PX_H,
      name: 'wrist-menu',
      resolution: 1.25,
      glass: true,
    });
    // Anchor the panel at its top edge so a shorter lobby menu grows down from the same line.
    this.panel.mesh.position.y = 0.36 - H_M / 2;
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => this.select(region);
    this.panel.attach(system);
    this.panel.mesh.renderOrder = 900;
    const material = this.panel.mesh.material as THREE.MeshBasicMaterial;
    material.depthTest = false;
    this.group.add(this.panel.mesh);
    this.orb = makeOrb();
    this.orbItem = {
      object: this.orb,
      onSelect: () => this.toggle(),
      onHover: () => this.orb.scale.setScalar(1.18),
      onLeave: () => this.orb.scale.setScalar(1),
    };
    system.add(this.orbItem);
    this.setOpen(false);
  }

  setAnchors(anchors: TeleportAnchor[], canLeave: boolean, place?: string): void {
    // A scene switch clears every interactive object, the menu included; it comes back here.
    this.panel.detach(this.system);
    this.panel.attach(this.system);
    this.system.remove(this.orbItem);
    this.system.add(this.orbItem);
    this.anchors = anchors.slice(0, 4);
    this.canLeave = canLeave;
    this.where = place;
    this.panel.invalidate();
  }

  refresh(_muted?: boolean, _smooth?: boolean, _hud?: boolean): void {
    this.panel.invalidate();
  }

  refreshCollab(): void {
    this.panel.invalidate();
  }

  setStatus(line: string): void {
    if (line === this.status) return;
    this.status = line;
    this.panel.invalidate();
  }

  /** Presses a control by its current label, through the same handler a ray uses (dev hooks). */
  press(label: string): boolean {
    if (!this.open) return false;
    this.panel.update();
    const action = this.actionsByLabel.get(label);
    if (!action) return false;
    action();
    return true;
  }

  /** World position of a control by its current label (dev hooks). */
  buttonWorldPosition(label: string): THREE.Vector3 | undefined {
    this.panel.update();
    const c = this.regionCentres.get(label);
    if (!c) return undefined;
    this.group.updateMatrixWorld(true);
    return this.panel.toWorld(c.x, c.y);
  }

  setOpen(open: boolean): void {
    if (open && !this.open) this.freePlaced = false;
    this.open = open;
    this.group.visible = open;
    if (open) this.panel.invalidate();
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  /** Positions the menu above the wrist (VR) or in front of the camera (desktop). */
  place(wrist: THREE.Object3D | undefined, camera: THREE.Camera, inXr: boolean): void {
    if (!this.open) return;
    this.panel.update();
    if (inXr && wrist) {
      wrist.updateWorldMatrix(true, false);
      const pos = wrist.getWorldPosition(new THREE.Vector3());
      const camPos = camera.getWorldPosition(new THREE.Vector3());
      pos.y += 0.12;
      this.group.position.copy(pos);
      const toCam = camPos.clone().sub(pos);
      this.group.rotation.set(0, Math.atan2(toCam.x, toCam.z), 0);
      this.group.rotateX(-0.35);
    } else if (inXr) {
      // No wrist (gaze and pinch, or hands without a tracked left wrist): stand the menu in
      // front of the user once and leave it there, so the eyes can move across it.
      if (this.freePlaced) return;
      this.freePlaced = true;
      const camPos = camera.getWorldPosition(new THREE.Vector3());
      const forward = levelForward(camera);
      this.group.position.copy(camPos).addScaledVector(forward, 0.75);
      this.group.position.y -= 0.05;
      this.group.rotation.set(0, Math.atan2(-forward.x, -forward.z), 0);
      this.group.rotateX(-0.12);
    } else {
      // Desktop: straight ahead along the view, pitch included, so the menu is on screen
      // whether the user looks level, up or down.
      const camPos = camera.getWorldPosition(new THREE.Vector3());
      const q = camera.getWorldQuaternion(new THREE.Quaternion());
      const dir = new THREE.Vector3(0, 0, -1).applyQuaternion(q);
      this.group.position.copy(camPos).addScaledVector(dir, 0.75);
      this.group.quaternion.copy(q);
    }
  }

  /**
   * The Menu orb: low on the left of the view, trailing the head's turn so it is always a
   * glance away but never in the middle of what is being looked at. Hidden when `show` is
   * false (desktop, or a controller with an X button).
   */
  placeOrb(camera: THREE.Camera, show: boolean): void {
    this.orb.visible = show;
    if (!show) {
      this.orbPlaced = false;
      return;
    }
    const camPos = camera.getWorldPosition(new THREE.Vector3());
    const forward = levelForward(camera);
    const right = new THREE.Vector3(-forward.z, 0, forward.x);
    const target = camPos
      .clone()
      .addScaledVector(forward, 0.55)
      .addScaledVector(right, -0.24)
      .add(new THREE.Vector3(0, -0.32, 0));
    if (!this.orbPlaced || this.orb.position.distanceTo(target) > 1.2) {
      this.orb.position.copy(target);
      this.orbPlaced = true;
    } else this.orb.position.lerp(target, 0.08);
    this.orb.lookAt(camPos);
  }

  /* ---------------- Drawing ---------------- */

  private tiles(): { you: Tile[]; comfort: Tile[]; room: Tile[] } {
    const a = this.actions;
    const muted = a.isMuted();
    const hand = a.handRaised();
    const follow = a.following();
    const presenting = a.presenting();
    const sharing = a.sharing();
    const invalidateAfter = (fn: () => unknown) => () => {
      fn();
      this.panel.invalidate();
    };
    return {
      you: [
        {
          id: 'mute',
          label: muted ? 'Unmute' : 'Mute',
          icon: muted ? 'micOff' : 'mic',
          on: muted,
          action: invalidateAfter(() => a.toggleMute()),
          hint: muted ? 'Muted' : 'Mic on',
        },
        {
          id: 'hand',
          label: hand ? 'Lower' : 'Hand',
          icon: 'hand',
          on: hand,
          action: invalidateAfter(() => a.toggleHand()),
          hint: hand ? 'Raised' : 'Raise hand',
        },
        {
          id: 'height',
          label: 'Reset height',
          icon: 'height',
          action: () => a.recalibrate(),
          hint: 'Recentre',
        },
      ],
      comfort: [
        {
          id: 'smooth',
          label: a.smoothLocomotion() ? 'Smooth on' : 'Smooth off',
          icon: 'walk',
          on: a.smoothLocomotion(),
          action: invalidateAfter(() => a.toggleSmoothLocomotion()),
          hint: 'Locomotion',
        },
        {
          id: 'hud',
          label: 'Debug HUD',
          icon: 'gauge',
          on: a.debugHud(),
          action: invalidateAfter(() => a.toggleDebugHud()),
          hint: 'Frame time',
        },
      ],
      room: [
        {
          id: 'annotate',
          label: a.annotating() ? 'Annotating' : 'Annotate',
          icon: 'pen',
          on: a.annotating(),
          action: invalidateAfter(() => a.toggleAnnotate()),
        },
        {
          id: 'point',
          label: a.pointing() ? 'Pointing' : 'Point',
          icon: 'pointer',
          on: a.pointing(),
          action: invalidateAfter(() => a.togglePoint()),
        },
        {
          id: 'follow',
          label: follow ? `Following ${follow.slice(0, 8)}` : 'Follow',
          icon: 'follow',
          on: !!follow,
          action: invalidateAfter(() => a.cycleFollow()),
        },
        { id: 'tidy', label: 'Tidy', icon: 'tidy', action: () => a.tidyRoom() },
        {
          id: 'present',
          label: presenting ? 'End' : 'Present',
          icon: 'present',
          on: presenting,
          action: invalidateAfter(() => a.togglePresent()),
        },
        {
          id: 'share',
          label: sharing ? 'Stop share' : 'Share',
          icon: 'share',
          on: sharing,
          enabled: a.canShare(),
          action: invalidateAfter(() => a.toggleShare()),
        },
        { id: 'pull', label: 'Pull screen', icon: 'pull', action: () => a.pullScreen() },
      ],
    };
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    this.actionsByLabel.clear();
    this.regionCentres.clear();
    const { you, comfort, room } = this.tiles();
    const inRoom = this.canLeave;
    const inner = PX_W - PAD * 2;

    // Lay out first to know the height, then draw the glass behind it.
    let y = PAD;
    const header = 150;
    const sectionGap = 26;
    const heading = 40;
    const goH = 92;
    const tileH = 112;
    const rows = (n: number, perRow: number) => Math.ceil(n / perRow);
    const youH = heading + tileH;
    const comfortH = heading + tileH;
    const roomH = inRoom ? heading + rows(room.length, 4) * (tileH + 14) - 14 : 0;
    const reactH = heading + 84;
    const leaveH = 84;
    const total =
      PAD +
      header +
      sectionGap +
      (this.anchors.length ? heading + goH + sectionGap : 0) +
      youH +
      sectionGap +
      comfortH +
      sectionGap +
      (inRoom ? roomH + sectionGap : 0) +
      reactH +
      sectionGap +
      leaveH +
      PAD;
    const height = Math.min(PX_H - 4, total);
    ctx.clearRect(0, 0, PX_W, PX_H);
    glassPanel(ctx, { x: 2, y: 2, w: PX_W - 4, h: height }, 44, { highlight: true });
    // A faint inner glow along the top of the glass.
    const glow = ctx.createLinearGradient(0, 2, 0, 160);
    glow.addColorStop(0, 'rgba(63,214,255,0.10)');
    glow.addColorStop(1, 'rgba(63,214,255,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(40, 4, PX_W - 80, 156);

    // Header: mark, name, where you are, the live line.
    this.drawMark(ctx, PAD + 34, y + 44, 30);
    text(ctx, 'CLINSPACEXR', PAD + 84, y + 40, {
      size: 15,
      weight: 700,
      colour: PALETTE.accentText,
    });
    text(ctx, this.where ?? (inRoom ? 'Study room' : 'Lobby'), PAD + 84, y + 84, {
      size: 30,
      weight: 600,
      display: true,
      maxWidth: inner - 84,
    });
    // The status line is 'N in place | M voice peers | mic unavailable': one chip each.
    const live = this.status.split(' | ').filter((part) => part.length > 0);
    this.drawStatus(ctx, PAD, y + 112, inner, live);
    y += header + sectionGap;

    // Go to: teleport tiles.
    if (this.anchors.length) {
      this.sectionHeading(ctx, 'GO TO', y);
      y += heading;
      const gap = 14;
      const w = (inner - gap * (this.anchors.length - 1)) / this.anchors.length;
      this.anchors.forEach((anchor, i) => {
        const r = { x: PAD + i * (w + gap), y, w, h: goH };
        const id = `go:${i}`;
        const hovered = panel.isHovered(id);
        glassPanel(ctx, r, 22, { hovered, hairline: false });
        this.drawIcon(
          ctx,
          'go',
          r.x + 34,
          r.y + r.h / 2,
          18,
          hovered ? inkFor(PALETTE.teal) : PALETTE.accentText,
        );
        text(ctx, anchor.label, r.x + 60, r.y + r.h / 2 + 8, {
          size: 17,
          weight: 600,
          maxWidth: r.w - 70,
        });
        this.register(panel, id, anchor.label, r, () => this.actions.teleport(anchor.position));
      });
      y += goH + sectionGap;
    }

    // You.
    this.sectionHeading(ctx, 'YOU', y);
    y += heading;
    this.tileRow(ctx, panel, you, y, tileH, 3);
    y += tileH + sectionGap;

    // Comfort: snap turn as a segmented control, then toggles.
    this.sectionHeading(ctx, 'COMFORT', y);
    y += heading;
    const snapW = inner * 0.36;
    const snapR = { x: PAD, y, w: snapW, h: tileH };
    glassPanel(ctx, snapR, 26, { hairline: false });
    this.drawIcon(ctx, 'turn', snapR.x + 40, snapR.y + 38, 18, PALETTE.accentText);
    text(ctx, 'Snap turn', snapR.x + 68, snapR.y + 44, { size: 15, weight: 600 });
    const snap = this.actions.snapAngle();
    [30, 45].forEach((deg, i) => {
      const segW = (snapW - 36 - 8) / 2;
      const r = { x: snapR.x + 18 + i * (segW + 8), y: snapR.y + 62, w: segW, h: 36 };
      const active = snap === deg;
      const id = `snap:${deg}`;
      const hovered = panel.isHovered(id);
      fillRoundRect(
        ctx,
        r,
        18,
        active ? 'rgba(63,214,255,0.28)' : hovered ? PALETTE.track : PALETTE.faint,
      );
      if (active) {
        ctx.strokeStyle = GLASS.highlightEdge;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      text(ctx, `${deg}°`, r.x + r.w / 2, r.y + 25, {
        size: 14,
        weight: 700,
        align: 'center',
        colour: active ? PALETTE.text : PALETTE.muted,
      });
      this.register(panel, id, `Snap ${deg}`, r, () => {
        this.actions.setSnapAngle(deg);
        this.panel.invalidate();
      });
    });
    // The old single button cycled; keep its label working for the dev hooks.
    this.actionsByLabel.set(`Snap ${snap}`, () => {
      this.actions.setSnapAngle(snap === 30 ? 45 : 30);
      this.panel.invalidate();
    });
    const restW = inner - snapW - 14;
    this.tileRow(ctx, panel, comfort, y, tileH, 2, PAD + snapW + 14, restW);
    y += tileH + sectionGap;

    // Collaborate (room only).
    if (inRoom) {
      this.sectionHeading(ctx, 'COLLABORATE', y);
      y += heading;
      for (let i = 0; i < room.length; i += 4) {
        this.tileRow(ctx, panel, room.slice(i, i + 4), y, tileH, 4);
        y += tileH + 14;
      }
      y += sectionGap - 14;
    }

    // React: round chips.
    this.sectionHeading(ctx, 'REACT', y);
    y += heading;
    const chipGap = 18;
    const chipW = (inner - chipGap * (REACTIONS.length - 1)) / REACTIONS.length;
    REACTIONS.forEach((r, i) => {
      const rect = { x: PAD + i * (chipW + chipGap), y, w: chipW, h: 72 };
      const id = `react:${r.reaction}`;
      const hovered = panel.isHovered(id);
      fillRoundRect(ctx, rect, 36, hovered ? 'rgba(63,214,255,0.22)' : PALETTE.faint);
      ctx.strokeStyle = hovered ? GLASS.highlightEdge : GLASS.edge;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      text(ctx, r.glyph, rect.x + rect.w / 2, rect.y + 46, {
        size: 18,
        weight: 700,
        align: 'center',
        display: true,
      });
      this.register(panel, id, r.label, rect, () => this.actions.react(r.reaction));
    });
    y += 84 + sectionGap;

    // Leave.
    const leave = { x: PAD, y, w: inner, h: leaveH };
    if (inRoom) {
      const hovered = panel.isHovered('leave');
      const g = ctx.createLinearGradient(leave.x, 0, leave.x + leave.w, 0);
      g.addColorStop(0, hovered ? 'rgba(255,93,115,0.45)' : 'rgba(255,93,115,0.26)');
      g.addColorStop(1, hovered ? 'rgba(255,93,115,0.25)' : 'rgba(255,93,115,0.12)');
      fillRoundRect(ctx, leave, 42, g);
      ctx.strokeStyle = 'rgba(255,140,155,0.75)';
      ctx.lineWidth = 2;
      ctx.stroke();
      this.drawIcon(ctx, 'exit', leave.x + 50, leave.y + leaveH / 2, 20, inkFor(PALETTE.crimson));
      text(ctx, 'Leave room', leave.x + 86, leave.y + 52, {
        size: 20,
        weight: 600,
        display: true,
        colour: PALETTE.text,
      });
      text(ctx, 'Back to the lobby', leave.x + leave.w - 30, leave.y + 50, {
        size: 14,
        colour: inkFor(PALETTE.crimson),
        align: 'right',
      });
      this.register(panel, 'leave', 'Leave room', leave, () => this.actions.leaveRoom());
    } else {
      fillRoundRect(ctx, leave, 42, PALETTE.faint);
      text(ctx, 'In the lobby', leave.x + leave.w / 2, leave.y + 52, {
        size: 17,
        weight: 600,
        align: 'center',
        colour: PALETTE.muted,
      });
      this.regionCentres.set('In the lobby', { x: leave.x + leave.w / 2, y: leave.y + leaveH / 2 });
    }
  }

  private register(
    panel: CanvasPanel,
    id: string,
    label: string,
    r: { x: number; y: number; w: number; h: number },
    action: () => void,
  ): void {
    panel.region({ id, ...r, data: { label } });
    this.actionsByLabel.set(label, action);
    this.regionCentres.set(label, { x: r.x + r.w / 2, y: r.y + r.h / 2 });
  }

  private sectionHeading(ctx: CanvasRenderingContext2D, label: string, y: number): void {
    text(ctx, label, PAD + 4, y + 24, { size: 12, weight: 700, colour: PALETTE.muted });
    ctx.font = font(12, 700);
    const w = ctx.measureText(label).width;
    const g = ctx.createLinearGradient(PAD + w + 20, 0, PX_W - PAD, 0);
    g.addColorStop(0, GLASS.edge);
    g.addColorStop(1, 'rgba(155,234,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(PAD + w + 20, y + 18, PX_W - PAD * 2 - w - 20, 1.5);
  }

  private tileRow(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    tiles: Tile[],
    y: number,
    h: number,
    perRow: number,
    x0 = PAD,
    width = PX_W - PAD * 2,
  ): void {
    const gap = 14;
    const w = (width - gap * (perRow - 1)) / perRow;
    tiles.forEach((t, i) => {
      const r = { x: x0 + i * (w + gap), y, w, h };
      const enabled = t.enabled !== false;
      const hovered = enabled && panel.isHovered(t.id);
      ctx.save();
      if (!enabled) ctx.globalAlpha = 0.4;
      if (t.on) {
        const g = ctx.createLinearGradient(0, r.y, 0, r.y + r.h);
        g.addColorStop(0, 'rgba(63,214,255,0.30)');
        g.addColorStop(1, 'rgba(61,123,255,0.16)');
        fillRoundRect(ctx, r, 26, g);
        ctx.strokeStyle = GLASS.highlightEdge;
        ctx.lineWidth = 2;
        ctx.stroke();
      } else {
        glassPanel(ctx, r, 26, { hovered, hairline: false });
      }
      const iconColour = t.on ? PALETTE.text : hovered ? inkFor(PALETTE.teal) : PALETTE.accentText;
      this.drawIcon(ctx, t.icon, r.x + r.w / 2, r.y + 40, 20, iconColour);
      text(ctx, t.label, r.x + r.w / 2, r.y + 84, {
        size: w < 170 ? 13 : 15,
        weight: 600,
        align: 'center',
        maxWidth: r.w - 16,
        colour: t.on ? PALETTE.text : PALETTE.textSoft,
      });
      if (t.hint && w >= 170)
        text(ctx, t.hint, r.x + r.w / 2, r.y + 104, {
          size: 11,
          align: 'center',
          maxWidth: r.w - 16,
          colour: t.on ? PALETTE.accentText : PALETTE.muted,
        });
      ctx.restore();
      if (enabled) this.register(panel, t.id, t.label, r, t.action);
      else this.regionCentres.set(t.label, { x: r.x + r.w / 2, y: r.y + r.h / 2 });
    });
  }

  private drawStatus(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    items: string[],
  ): void {
    let cx = x;
    let cy = y;
    for (const item of items) {
      const lost = /lost|unavailable/i.test(item);
      ctx.font = font(13, 600);
      const w = ctx.measureText(item).width + 46;
      if (cx + w > x + width) {
        cx = x;
        cy += 40;
      }
      fillRoundRect(ctx, { x: cx, y: cy, w, h: 32 }, 16, PALETTE.faint);
      ctx.fillStyle = lost ? PALETTE.amber : PALETTE.forest;
      ctx.beginPath();
      ctx.arc(cx + 18, cy + 16, 5, 0, Math.PI * 2);
      ctx.fill();
      text(ctx, item, cx + 32, cy + 22, { size: 13, weight: 600, colour: PALETTE.textSoft });
      cx += w + 10;
    }
  }

  private drawMark(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = inkFor(PALETTE.teal);
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y, r, -0.4, Math.PI * 1.5);
    ctx.stroke();
    ctx.strokeStyle = PALETTE.accentText;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.62, 0.8, Math.PI * 2.4);
    ctx.stroke();
    ctx.fillStyle = PALETTE.text;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.18, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** Line icons, centred at (x, y), about 2s across. */
  private drawIcon(
    ctx: CanvasRenderingContext2D,
    icon: Icon,
    x: number,
    y: number,
    s: number,
    colour: string,
  ): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.strokeStyle = colour;
    ctx.fillStyle = colour;
    ctx.lineWidth = Math.max(2, s * 0.13);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const line = (pts: [number, number][]) => {
      ctx.beginPath();
      pts.forEach(([px, py], i) => (i ? ctx.lineTo(px * s, py * s) : ctx.moveTo(px * s, py * s)));
      ctx.stroke();
    };
    const circle = (cx: number, cy: number, r: number, fill = false) => {
      ctx.beginPath();
      ctx.arc(cx * s, cy * s, r * s, 0, Math.PI * 2);
      if (fill) ctx.fill();
      else ctx.stroke();
    };
    switch (icon) {
      case 'go':
        circle(0, 0, 0.9);
        line([
          [-0.3, -0.4],
          [0.25, 0],
          [-0.3, 0.4],
        ]);
        break;
      case 'mic':
      case 'micOff':
        ctx.beginPath();
        ctx.roundRect(-0.32 * s, -0.95 * s, 0.64 * s, 1.15 * s, 0.32 * s);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(0, -0.1 * s, 0.62 * s, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();
        line([
          [0, 0.52],
          [0, 0.9],
        ]);
        if (icon === 'micOff')
          line([
            [-0.8, -0.9],
            [0.8, 0.9],
          ]);
        break;
      case 'hand':
        line([
          [-0.45, 0.9],
          [-0.55, 0.1],
          [-0.55, -0.4],
        ]);
        for (const fx of [-0.25, 0.05, 0.35])
          line([
            [fx, 0.05],
            [fx, -0.85 + Math.abs(fx) * 0.2],
          ]);
        line([
          [0.35, 0.05],
          [0.55, -0.2],
          [0.6, 0.4],
          [0.2, 0.9],
          [-0.45, 0.9],
        ]);
        break;
      case 'height':
        line([
          [0, -0.9],
          [0, 0.9],
        ]);
        line([
          [-0.35, -0.55],
          [0, -0.9],
          [0.35, -0.55],
        ]);
        line([
          [-0.35, 0.55],
          [0, 0.9],
          [0.35, 0.55],
        ]);
        line([
          [-0.8, 0],
          [-0.45, 0],
        ]);
        line([
          [0.45, 0],
          [0.8, 0],
        ]);
        break;
      case 'turn':
        ctx.beginPath();
        ctx.arc(0, 0, 0.75 * s, -0.2 * Math.PI, 1.3 * Math.PI);
        ctx.stroke();
        line([
          [0.35, -0.95],
          [0.62, -0.5],
          [0.1, -0.35],
        ]);
        break;
      case 'walk':
        circle(0.1, -0.75, 0.2, true);
        line([
          [0.05, -0.4],
          [-0.1, 0.2],
          [-0.5, 0.9],
        ]);
        line([
          [-0.1, 0.2],
          [0.35, 0.5],
          [0.4, 0.9],
        ]);
        line([
          [-0.45, -0.05],
          [0.05, -0.35],
          [0.5, -0.05],
        ]);
        break;
      case 'gauge':
        ctx.beginPath();
        ctx.arc(0, 0.25 * s, 0.85 * s, Math.PI, 2 * Math.PI);
        ctx.stroke();
        line([
          [0, 0.25],
          [0.45, -0.35],
        ]);
        circle(0, 0.25, 0.12, true);
        break;
      case 'pen':
        line([
          [-0.75, 0.75],
          [-0.55, 0.15],
          [0.45, -0.85],
          [0.85, -0.45],
          [-0.15, 0.55],
          [-0.75, 0.75],
        ]);
        break;
      case 'pointer':
        line([
          [-0.6, -0.85],
          [0.6, 0],
          [0.05, 0.12],
          [-0.15, 0.85],
          [-0.6, -0.85],
        ]);
        break;
      case 'follow':
        circle(-0.35, -0.45, 0.28);
        ctx.beginPath();
        ctx.arc(-0.35 * s, 0.75 * s, 0.6 * s, Math.PI * 1.1, Math.PI * 1.9);
        ctx.stroke();
        line([
          [0.35, -0.2],
          [0.85, 0.15],
          [0.35, 0.5],
        ]);
        break;
      case 'tidy':
        for (const [gx, gy] of [
          [-0.45, -0.45],
          [0.45, -0.45],
          [-0.45, 0.45],
          [0.45, 0.45],
        ] as [number, number][]) {
          ctx.beginPath();
          ctx.roundRect((gx - 0.3) * s, (gy - 0.3) * s, 0.6 * s, 0.6 * s, 0.12 * s);
          ctx.stroke();
        }
        break;
      case 'present':
        ctx.beginPath();
        ctx.roundRect(-0.9 * s, -0.8 * s, 1.8 * s, 1.15 * s, 0.12 * s);
        ctx.stroke();
        line([
          [0, 0.35],
          [0, 0.9],
        ]);
        line([
          [-0.4, 0.9],
          [0.4, 0.9],
        ]);
        line([
          [-0.45, 0.0],
          [-0.1, -0.35],
          [0.15, -0.1],
          [0.5, -0.5],
        ]);
        break;
      case 'share':
        ctx.beginPath();
        ctx.roundRect(-0.9 * s, -0.75 * s, 1.8 * s, 1.2 * s, 0.12 * s);
        ctx.stroke();
        line([
          [0, 0.2],
          [0, -0.45],
        ]);
        line([
          [-0.3, -0.2],
          [0, -0.45],
          [0.3, -0.2],
        ]);
        line([
          [-0.4, 0.85],
          [0.4, 0.85],
        ]);
        break;
      case 'pull':
        ctx.beginPath();
        ctx.roundRect(-0.85 * s, -0.9 * s, 1.7 * s, 0.9 * s, 0.12 * s);
        ctx.stroke();
        line([
          [0, 0.1],
          [0, 0.9],
        ]);
        line([
          [-0.32, 0.6],
          [0, 0.9],
          [0.32, 0.6],
        ]);
        break;
      case 'exit':
        line([
          [0.15, -0.85],
          [-0.7, -0.85],
          [-0.7, 0.85],
          [0.15, 0.85],
        ]);
        line([
          [-0.2, 0],
          [0.85, 0],
        ]);
        line([
          [0.5, -0.35],
          [0.85, 0],
          [0.5, 0.35],
        ]);
        break;
    }
    ctx.restore();
  }

  private select(region: Region): void {
    const label = (region.data as { label?: string } | undefined)?.label;
    const action = label ? this.actionsByLabel.get(label) : undefined;
    action?.();
  }
}
